import { reactive, toRefs, computed } from 'vue';
import Cookies from 'cookie-universal';
import { remove } from '@/components/commons/MiniLodash';
import { http } from '@/pixel/index';
import { gql } from '@apollo/client/core';
import {
  trackEvent, //
  getCartQuantity,
  checkLoggedIn,
  executeQuery,
  executeMutation,
} from '@/components/commons/utils.js';
import { trackCartError } from '@/components/cart/utils.js';
import CountryUtils from '@/components/commons/CountryUtils.js';
// import Chat from '@/components/commons/Chat.js';
// eslint-disable-next-line import/extensions

import UserService from '@/services/UserService';
import LocationService from '@/services/LocationService';
import SessionService from '@/services/SessionService';
import MessagingService from '@/services/MessagingService';
import Tracking from '@/components/commons/Tracking';
import useFavorite from '@/compositions/useFavorite';
import md5 from 'blueimp-md5';

const COMMON_DATA_QUERY = gql`
  query COMMON_DATA {
    commonData {
      country {
        id
        code
      }
      currency {
        id
        code
      }
      categories {
        id
        name
        displayName
        url
        parent {
          id
        }
        path
        displayImage {
          url
          key
        }
        sort
      }
      primaryRooms {
        id
        value
        name
        displayName
        url
      }
      urls {
        static
        images
        getProducts
        getOrders
        browseReviews
        getOrderTracking
        subscription
        switchCurrency
        getProductReviews
      }
      promotion {
        id
        name
        caName
        startDate
        endDate
        isoEndDate
        saleMessage
        caSaleMessage
        perCountryConfig
      }
      financing {
        affirmEnabled
        affirmMaxAmount
        affirmMinAmount
        affirmKey
        affirmUrl
        paybrightEnabled
        paybrightMaxAmount
        paybrightMinAmount
        paybrightKey
        paybrightUrl
        paybrightModalUrl
      }
      clearanceNavigationEnabled
      giftCardNavigationEnabled
      hasClearanceProductsCA
      hasClearanceProductsUS
      additionalNavigation {
        id
        items {
          parentId
          insertAfter
          id
          name
          url
        }
        name
        url
        insertAfter
      }
      bannerNavigation {
        name
        url
      }
      footerNavigation {
        name
        url
        items {
          name
          url
        }
      }
    }
  }
`;

const LOAD_CART_QUERY = gql`
  query STORE_MINI_CART {
    storeMiniCartGet {
      success
      cartInfo {
        countryId
        countryCode
        currencyId
        currencySymbol
        cartItems {
          productId
          quantity
        }
        summary {
          subtotalWithSaleSavings
          items {
            productId
            productTitle
            productUrl
            quantity
            originalPrice
            productPackagedItemQuantity
            price
            productImage {
              key
              url
              alt
            }
            isOnSale
            isOnClearance
            countries
            isEtaAtRisk
            categoryPath
          }
          discounts {
            setDiscountId
            code
            quantity
            discountAmountWithCurrency
          }
        }
        orderInfos {
          subtotal
          total
          tax
          taxRate
          isTaxExempt
          shippingOptionId
          shippingCost
          checkoutGroup
          items {
            productId
            quantity
            productPackagedItemQuantity
            productSkuNo
            productTitle
            productUrl
            price
            priceCents
            originalPrice
            originalPriceCents
            originalAmount
            originalAmountCents
            amount
            amountCents
            isEtaAtRisk
            isOnSale
            isOnClearance
          }
          orderDiscounts {
            code
            type
            percentage
            amount
            minimumPurchase
            discountAmount
            discountAmountCents
            currencySymbol
            isDynamicDeliveryCostDiscount
            quantity
          }
        }
        shippingOptionsSelected
        trade
        useCredits
        fullyPaidByStoreCredit
        hasAvailableCredit
        availableCredit
        availableCreditCents
        creditsToUse
        creditsToUseCents
        creditsToUse
        creditsToUseCents
        orderTotalLessCredits
        orderTotalLessCreditsCents
        totalWithTaxEstimate
        totalWithTaxEstimateCents
        tax
        total
        calculationDate
        splittable
        enablePayBright
        enablePaypal
        enableStripe
        enableResolvePay
        enableAffirm
      }
    }
  }
`;

const state = reactive({
  user: {
    cell: '',
    email: '',
    expired: false,
    firstName: '',
    isTrade: false,
    lastLoginDate: '',
    lastLoginIp: '',
    lastName: '',
    registerIp: '',
    registered: false,
    sessionId: '',
    sessionIp: '',
    verified: false,
    verifyDate: '',
    verifyIp: '',
  },
  geoLocation: {
    accuracyRadius: '',
    countryId: '',
    countryCode: '',
    regionId: '',
    regionCode: '',
    city: '',
    currencyId: '',
    zipcode: '',
  },
  zipLocation: {
    accuracyRadius: '',
    countryId: '',
    countryCode: '',
    regionId: '',
    regionCode: '',
    city: '',
    currencyId: '',
    zipcode: '',
  },
  isOffice: false,
  client: true,
  initialized: false,
  countryId: LocationService.getCountryId(),
  currencyId: LocationService.getCurrencyId(),
  urls: {},
  notifyCartItemsAdded: false,
  ugcPixleeEnabled: true,
  blackFridayContactEnabled: true,
  shippingDiscount: {},
  activeFileProducts: [],
  currentRoom: '',
  siteMessage: null,
  etaMessage: null,
  bannerMessage: null,
  financing: {
    affirmEnabled: false,
    affirmMaxAmount: null,
    affirmMinAmount: null,
    affirmKey: null,
    affirmUrl: null,
    paybrightEnabled: false,
    paybrightMaxAmount: null,
    paybrightMinAmount: null,
    paybrightKey: null,
    paybrightUrl: null,
    paybrightModalUrl: null,
  },
  payment: {
    stripe: {
      usPublishableKey: null,
      canadaPublishableKey: null,
    },
    paypal: {
      clientId: null,
    },
  },
  csPhoneWeekdaysHoursStart: '',
  csPhoneWeekdaysHoursEnd: '',
  csPhoneWeekendHoursStart: '',
  csPhoneWeekendHoursEnd: '',
  csChatWeekdaysHoursStart: '',
  csChatWeekdaysHoursEnd: '',
  csChatWeekendHoursStart: '',
  csChatWeekendHoursEnd: '',
  csChatOverlayWeekdaysHoursStart: '',
  csChatOverlayWeekdaysHoursEnd: '',
  csChatOverlayWeekendHoursStart: '',
  csChatOverlayWeekendHoursEnd: '',
  csMessageResponseTimeDescription: 'Due to increased volume, our current response time is within 4 days.',
  initializedSessionData: false,
  cartFetched: false,
  primaryRooms: [],
  menuItems: [],
  bannerNavigationLinks: [],
  showContactBanner: true,
  showMobileCategoryMenu: true,
  enableSearchSuggestions: true,
  cartCount: 0,
  cart: null,
  currentRoute: {},
  storefront: true,
  showPaybrightModal: false,
  showAffirmModal: false,
  payBrightMoreInfoUrl: null,
  affirmModalUrl: null,
  promotion: {},
  roomIdeasTracking: {},
  customerToken: '',
  categories: [],
  searchQuery: '',
  footerNavigationLinks: [],
});

let router = {};

export default function useCommon(_router) {
  if (_router) {
    router = _router;
  }

  async function fetchData() {
    try {
      const result = await executeQuery(COMMON_DATA_QUERY);
      updateCommonData(JSON.parse(JSON.stringify(result.commonData)));
    } catch (error) {
      console.error('fetchData', error);
    }
  }

  async function chat() {
    const Chat = (await import('@/components/commons/Chat.js')).default;
    const chatAfterHours = router.resolve({ name: 'message', query: { 'after-hours': true } }).href;
    Chat.start(chatAfterHours);
  }

  async function search(query = '') {
    if (typeof query !== 'string') {
      return;
    }
    window.location = router.resolve({ name: 'search', query: { q: query.trim() } }).href;
  }

  async function miniCartUpdateQuantity({ productId, quantity }) {
    try {
      await executeMutation(
        gql`
          mutation UPDATE_CART_QUANTITY_MINI_CART($items: [CartItemInput]) {
            updateCartQuantity(items: $items) {
              success
              cartItems {
                productId
                quantity
              }
            }
          }
        `,
        {
          items: [
            {
              productId: Number.parseInt(productId, 10),
              quantity: Number.parseInt(quantity, 10),
            },
          ],
        },
      );
    } catch (e) {
      trackCartError('Unable to update cart quantity.', e);
      throw e;
    }
  }

  async function openMiniCart() {
    const data = await loadCartData();
    if (data) {
      UserService.setCart(data.cartInfo);
    }
    updateMiniCart({ cart: data.cartInfo, cartCount: getCartQuantity() });
    updateCartFetched(true);
  }

  async function checkout() {
    const eventId = md5(state.user.sessionId + window.location.href);
    trackEvent('Checkout', 'Initiate Checkout', '', '', {
      hashedId: eventId,
    });
    Tracking.track('INITIATE_CHECKOUT', {
      email: state.user.email,
    });
    window.location = router.resolve({ name: 'cart' }).href;
  }

  async function changeCountry({ country, shouldReload = true }) {
    LocationService.setCountry(CountryUtils[country] || CountryUtils.US, {
      shouldReload,
    });
  }

  async function closeBanner() {
    if (state.shippingDiscount) {
      const date = new Date();
      date.setDate(date.getDate() + 1);
      Cookies().set('article-shipping-discount', true, { expires: date });
    }
  }

  async function updateLocation({ zip, shouldReload }) {
    loadMessaging(zip);
    return getSessionData({ zip, shouldReload });
  }

  async function loadMessaging(zip) {
    const { common, messaging } = await MessagingService.fetchMessaging(zip);

    window.livechatOverlayEnabled = common.livechatOverlayEnabled;

    updateSiteMessage(messaging.message?.text);
    updateEtaMessage(messaging.message?.etaText);

    updateShippingDiscount({
      message: messaging.legacyShippingDiscountMessage,
      details: messaging.legacyShippingDiscountDetails,
      baselineShippingCostDiscounted: messaging.baselineShippingCostDiscounted,
      shippingDiscountThreshold: messaging.shippingDiscountThreshold,
      baselineShippingCost: messaging.baselineShippingCost,
    });

    updateUgcPixleeEnabled(!!common.ugcPixleeEnabled);
    updateBlackFridayContactEnabled(!!common.blackFridayContactEnabled);
    updateCsMessageResponseTime(common.csMessageResponseTimeDescription);
    updateCsSupportHours({
      csPhoneWeekdaysHoursStart: common.csPhoneWeekdaysHoursStart,
      csPhoneWeekdaysHoursEnd: common.csPhoneWeekdaysHoursEnd,
      csPhoneWeekendHoursStart: common.csPhoneWeekendHoursStart,
      csPhoneWeekendHoursEnd: common.csPhoneWeekendHoursEnd,
      csChatWeekdaysHoursStart: common.csChatWeekdaysHoursStart,
      csChatWeekdaysHoursEnd: common.csChatWeekdaysHoursEnd,
      csChatWeekendHoursStart: common.csChatWeekendHoursStart,
      csChatWeekendHoursEnd: common.csChatWeekendHoursEnd,
      csChatOverlayWeekdaysHoursStart: common.csChatOverlayWeekdaysHoursStart,
      csChatOverlayWeekdaysHoursEnd: common.csChatOverlayWeekdaysHoursEnd,
      csChatOverlayWeekendHoursStart: common.csChatOverlayWeekendHoursStart,
      csChatOverlayWeekendHoursEnd: common.csChatOverlayWeekendHoursEnd,
    });
  }

  async function loadCartData() {
    const result = await executeQuery(LOAD_CART_QUERY, null, {
      fetchPolicy: 'network-only',
    });
    return result.storeMiniCartGet ?? {};
  }

  async function notifyCartItemsAddedAction() {
    updateNotifyCartItemsAdded(true);
    const data = await loadCartData();
    if (data) {
      UserService.setCart(data.cartInfo);
    }
    updateMiniCart({ cart: data.cartInfo, cartCount: getCartQuantity() });
    updateCartFetched(true);
  }

  async function loadProductsForFile(id) {
    updateActiveFileProducts([]);
    const filter = `filter[fileProduct]=file.id==${id};product.status!='DISCONTINUED'`;
    const include = `include=product`;
    const fields = `fields[product]=id,imageUrl,popularity,title,basePrice,salePrice,url,status,skuNo`;
    const response = await fetch(`${import.meta.env.VITE_KRAKEN_MEDIA_API}/fileProduct?${filter}&${include}&${fields}`);
    const responseJson = await response.json();
    updateActiveFileProducts(JSON.parse(JSON.stringify(responseJson)).included);
  }

  async function getSessionData(payload) {
    const defaultPayload = {
      zip: '',
      shouldReload: true,
    };

    const { zip, shouldReload } = {
      ...defaultPayload,
      ...(payload || {}),
    };

    try {
      const {
        user, //
        customerToken,
        office,
        geoLocation,
        zipLocation,
        error,
      } = await SessionService.ping(zip, UserService.getCustomerToken());

      if (error) {
        return {};
      }

      if (user?.expired) {
        UserService.setUser(null);
        updateUser(null);
      } else {
        UserService.setUser(user);
        updateUser(user);
      }

      UserService.setCustomerToken(customerToken);
      updateCustomerToken(customerToken);

      if (office != null) {
        LocationService.setOffice(office);
        updateOffice(office);
      }

      if (!zip || (zip && zipLocation)) {
        LocationService.setLocation(
          {
            geoLocation,
            zipLocation,
          },
          shouldReload,
        );

        if (geoLocation != null) {
          updateGeoLocation(geoLocation);
        }

        if (zipLocation != null) {
          updateZipLocation(zipLocation);
        }
      }

      updateInitializedSessionData(true);

      refreshCartData();
      refreshFavorites();

      checkLoggedIn();

      return {
        user,
        office,
        geoLocation,
        zipLocation,
      };
    } catch (error) {
      Tracking.sentryCaptureError(error);
    }

    updateInitializedSessionData(true);

    return {};
  }

  function refreshUserData() {
    try {
      UserService.refreshUser();

      const user = UserService.getUser();

      if (user?.expired) {
        updateUser(null);
      } else {
        updateUser(user);
      }

      refreshCartData();
      refreshFavorites();

      const { getFavorite } = useFavorite();
      getFavorite();
    } catch (error) {
      Tracking.sentryCaptureError(error);
    }
  }

  function refreshCartData() {
    try {
      UserService.refreshCart();

      updateMiniCartCount(getCartQuantity());
      updateMiniCart({ cart: UserService.getCart(), cartCount: getCartQuantity() });
    } catch (error) {
      Tracking.sentryCaptureError(error);
    }
  }

  async function refreshFavorites() {
    const { getFavorite } = useFavorite();
    getFavorite();
  }

  function getUser() {
    if (UserService.getUser()) {
      updateUser(UserService.getUser());
    }

    if (UserService.getSessionId()) {
      http.attach({
        __i: UserService.getSessionId(),
      });
    }

    if (UserService.getCustomerToken()) {
      http.attach(
        {
          __t: UserService.getCustomerToken(),
        },
        false,
      );
    }
  }

  function setUser(payload) {
    UserService.setUser(payload);
    updateUser(payload);
  }

  function setCustomerToken(payload) {
    UserService.setCustomerToken(payload);
    updateCustomerToken(payload);
  }

  function updateCurrentRoom(payload) {
    state.currentRoom = payload;
  }

  function updateActiveFileProducts(products) {
    state.activeFileProducts = products;
  }

  function updateCommonData(common) {
    const {
      categories, //
      primaryRooms,
      urls,
      promotion,
      hasClearanceProductsCA,
      hasClearanceProductsUS,
      giftCardNavigationEnabled,
      financing,
      additionalNavigation,
      bannerNavigation,
      footerNavigation,
    } = common;

    state.countryId = LocationService.getCountryId();
    state.currencyId = LocationService.getCurrencyId();

    const hasClearance = LocationService.isCanada() ? hasClearanceProductsCA : hasClearanceProductsUS;

    categories.forEach((category) => {
      if (category.parent) {
        category.parentId = category.parent.id;
      }
    });
    state.categories = categories;
    state.menuItems = formatMenuItems(categories, primaryRooms, {
      // showPromotion: !!promotion,
      // showPromotion: promotion,
      promotion,
      showGiftCards: giftCardNavigationEnabled,
      showClearance: hasClearance,
      additionalNavigation,
    });
    state.clearanceNavigationEnabled = hasClearance;
    state.urls = urls;
    state.promotion = promotion;
    state.bannerNavigationLinks = bannerNavigation || [];
    state.footerNavigationLinks = footerNavigation || [];
    Object.assign(state.financing, financing);
  }

  function updateIntialized(initialized) {
    state.initialized = initialized;
  }

  function updateShippingDiscount(shippingDiscount) {
    state.shippingDiscount = shippingDiscount;

    if (shippingDiscount && shippingDiscount.message) {
      const customerClosedBanner = Cookies().get('article-shipping-discount', { parseJSON: false }) && false;
      if (!customerClosedBanner && !state.user?.isTrade) {
        state.bannerMessage = shippingDiscount.message;
      } else {
        state.bannerMessage = null;
      }
    } else {
      state.bannerMessage = null;
    }
  }

  function updateSiteMessage(message) {
    state.siteMessage = message;
  }

  function updateEtaMessage(message) {
    state.etaMessage = message;
  }

  function updateMiniCart({ cart, cartCount }) {
    if (cart) {
      state.cart = cart;
    }

    if (typeof cartCount == 'number') {
      state.cartCount = cartCount;
    }
  }

  function updateMiniCartCount(cartCount) {
    state.cartCount = cartCount;
  }

  function updateCurrency(currencyId) {
    state.currencyId = currencyId;
    state.countryId = CountryUtils.getCountryByCurrencyId(currencyId).countryId;
  }

  function updateCountry(countryId) {
    state.countryId = countryId;
    state.currencyId = CountryUtils.getCountryById(countryId).currencyId;
  }

  function updateNotifyCartItemsAdded(notify) {
    state.notifyCartItemsAdded = notify;
  }

  function updateUgcPixleeEnabled(payload) {
    state.ugcPixleeEnabled = payload;
  }

  function updateBlackFridayContactEnabled(payload) {
    state.blackFridayContactEnabled = payload;
  }

  function updatePaybrightUrl(payload) {
    state.payBrightMoreInfoUrl = payload;
  }

  function togglePaybrightModal() {
    state.showPaybrightModal = !state.showPaybrightModal;
  }

  function toggleAffirmModal() {
    state.showAffirmModal = !state.showAffirmModal;
  }

  function updateAffirmUrl(payload) {
    state.affirmModalUrl = payload;
  }

  function updateCsSupportHours(payload) {
    if (payload && Object.keys(payload).length) {
      Object.entries(payload).forEach(([key, value]) => {
        state[key] = value;
      });
    }
  }

  function updateCsMessageResponseTime(responseTimeDescription) {
    state.csMessageResponseTimeDescription = responseTimeDescription;
  }

  function updateUser(payload) {
    state.user = payload;
  }

  function updateCustomerToken(payload) {
    state.customerToken = payload;
  }

  function updateGeoLocation(payload) {
    state.geoLocation = payload;
  }

  function updateZipLocation(payload) {
    state.zipLocation = payload;
  }

  function updateOffice(payload) {
    state.isOffice = payload;
  }

  function updateInitializedSessionData(payload) {
    state.initializedSessionData = payload;
  }

  function updateCartFetched(payload) {
    state.cartFetched = payload;
  }

  function updateCurrentRoute(payload) {
    state.currentRoute = payload;
  }

  function updateRoomIdeasTracking(payload) {
    state.roomIdeasTracking = payload;
  }

  function setSearchQuery(payload) {
    state.searchQuery = payload;
  }

  function formatMenuItems(initialCategories, initialRooms, { promotion, showClearance, showGiftCards, additionalNavigation }) {
    if (!initialCategories) {
      return [];
    }

    const groupedCategories = initialCategories.reduce((acc, cat) => {
      const { parentId } = cat;
      if (!acc[parentId]) {
        acc[parentId] = [];
      }
      acc[parentId].push(cat);
      return acc;
    }, {});
    const categories = initialCategories.filter((category) => !category.parentId);
    categories.forEach((category) => {
      const allCategory = {
        ...category,
        name: category.displayName || category.name,
        sort: 0,
      };
      const subCategories = [allCategory, ...(groupedCategories[category.id] || [])];
      category.items = subCategories.length > 1 ? subCategories : [];
    });

    const newCategories = remove(categories, (category) => category.name === 'NEW');
    const roomCategories = remove(categories, (category) => category.name.toLowerCase() === 'rooms');
    roomCategories.forEach((category) => category.items.sort((a, b) => a.sort - b.sort));

    categories.sort((a, b) => a.sort - b.sort);
    categories.forEach((category) => category.items.sort((a, b) => a.sort - b.sort));

    if (roomCategories?.length) {
      const roomCategory = roomCategories[0];
      roomCategory.url = '';
      roomCategory.items = roomCategory.items.filter((category) => category.name.toLowerCase() !== 'rooms');
      categories.push(roomCategory);
    }

    if (showGiftCards) {
      categories.push({
        id: 'giftcards',
        name: 'Gift Cards',
        url: router.resolve({ name: 'giftCardBuy' }).href,
      });
    }

    if (!!promotion || showClearance) {
      const saleMenu = {
        id: 'sale',
        name: 'Sale',
        url: router.resolve({ name: 'sale' }).href,
      };

      if (promotion) {
        if (!showClearance) {
          saleMenu.url = router.resolve({ name: 'currentPromotion' }).href;
        } else {
          saleMenu.items = showClearance
            ? [
              {
                id: 'sale',
                parentId: 'sale',
                name: 'All Sale',
                url: router.resolve({ name: 'sale' }).href,
              },
            ]
            : [];

          saleMenu.items.push({
            id: 'promotion',
            parentId: 'sale',
            name: (LocationService.isCanada() && promotion.caName) || promotion.name,
            url: router.resolve({ name: 'currentPromotion' }).href,
          });

          if (showClearance) {
            saleMenu.items.push({
              id: 'clearance',
              parentId: 'sale',
              name: 'Clearance',
              url: router.resolve({ name: 'clearance' }).href,
            });
          }
        }
      }

      categories.push(saleMenu);
    }

    if (newCategories && newCategories.length) {
      const newCategory = newCategories[0];
      newCategory.items = [];
      categories.push(newCategory);
    }

    if (additionalNavigation) {
      const catMap = {};
      categories.forEach((c) => (catMap[c.name] = c));

      additionalNavigation
        .sort((a, b) => b.insertAfter || 0 - a.insertAfter || 0)
        .forEach((newCategory) => {
          if (catMap[newCategory.name]) {
            // if there is a match
            const items = catMap[newCategory.name].items;
            newCategory.items
              .sort((a, b) => b.insertAfter || 0 - a.insertAfter || 0)
              .forEach((newItem) => {
                let insertAt = newItem.insertAfter ?? items.length;
                if (insertAt < 0) {
                  insertAt = 0;
                }

                items.splice(insertAt, 0, newItem);
              });
          } else {
            newCategory.items.sort((a, b) => (a.insertAfter ?? Number.MAX_SAFE_INTEGER) - (b.insertAfter ?? Number.MAX_SAFE_INTEGER));
            const defaultIndex = (categories.findIndex((c) => c.name.toLowerCase() === 'new') + 1) || categories.length;
            let insertAt = newCategory.insertAfter ?? defaultIndex;
            if (insertAt < 0) {
              insertAt = 0;
            }
            categories.splice(insertAt, 0, newCategory);
          }
        });
    }

    return categories;
  }

  const currentCountry = computed(() => {
    const country = { ...CountryUtils.getCountryByCurrencyId(state.currencyId) };
    return Object.assign(country, { alt: `${country.name} Flag` });
  });

  // Match the current route to a menu item
  const routeMenuItem = computed(() => {
    const route = Object.assign({}, state.currentRoute);
    const rooms = ['living room', 'dining room', 'bedroom', 'outdoor', 'office', 'entryway'];
    const sales = ['all sale', 'current sale', 'clearance sale'];
    let flatMenuItems = state.menuItems?.map((item) => (item.items && item.items.length ? item.items : item))?.flat() ?? [];
    flatMenuItems = flatMenuItems
      .map((item) => (item.items && item.items.length ? item.items : item))
      .flat()
      .map((flatMenuItem) => {
        const name = flatMenuItem.name.toLowerCase();
        if (sales.indexOf(name) >= 0 || name.includes('sale')) {
          flatMenuItem.parentMenuItem = 'sale';
        } else if (rooms.indexOf(name) >= 0) {
          flatMenuItem.parentMenuItem = 'room';
        } else {
          flatMenuItem.parentMenuItem = 'product';
        }
        return flatMenuItem;
      });

    const matchingItems = flatMenuItems.filter((item) => {
      if (route.name === 'browse' && route.params && route.params.id) {
        return `${item.id}` === route.params.id;
      }
      if (route.name === 'room' && route.params && route.params.id) {
        return `${item.id}` === route.params.id;
      }
      return `${item.id}` === route.name;
    });

    return matchingItems.length ? matchingItems[0] : null;
  });

  const isRegisteredUser = computed(() => {
    return state.user?.registered ?? false;
  });

  const isVerifiedUser = computed(() => {
    return state.user?.verified ?? false;
  });

  const roomIdeasTracking = computed(() => {
    return state.roomIdeasTracking;
  });

  const currentPromotion = computed(() => {
    if (state.promotion) {
      const sales = {};
      if (state.promotion.perCountryConfig && LocationService.isCanada()) {
        sales.name = state.promotion.caName;
        sales.caption = state.promotion.caSaleMessage;
        sales.time = state.promotion.isoEndDate;
        sales.date = state.promotion.endDate;
      } else {
        sales.name = state.promotion.name;
        sales.caption = state.promotion.saleMessage;
        sales.time = state.promotion.isoEndDate;
        sales.date = state.promotion.endDate;
      }

      sales.type = 'blockInter1';

      return sales;
    }
    return {};
  });

  return {
    ...toRefs(state), //
    fetchData,
    loadMessaging,
    updateCurrentRoom,
    loadProductsForFile,
    updateRoomIdeasTracking,
    getUser,
    getSessionData,
    refreshUserData,
    refreshCartData,
    refreshFavorites,
    changeCountry,
    updateIntialized,
    chat,
    search,
    openMiniCart,
    checkout,
    closeBanner,
    isVerifiedUser,
    currentCountry,
    routeMenuItem,
    notifyCartItemsAddedAction,
    updateNotifyCartItemsAdded,
    updateMiniCartCount,
    updateMiniCart,
    updateCurrentRoute,
    togglePaybrightModal,
    toggleAffirmModal,
    currentPromotion,
    setUser,
    setCustomerToken,
    setSearchQuery,
    updateLocation,
    updateAffirmUrl,
    updatePaybrightUrl,
    miniCartUpdateQuantity,
    updateCurrency,
    updateCountry,
  };
}
